import { Container, Row, Col, Table } from 'react-bootstrap';

import { ProjectRoute } from "../index";
import { SummaryLine } from '../../../data';
import { StoreButtons, CaptionedImage } from '../../../components';

const MarvelSnapRoute = (): JSX.Element => {
    const summary: SummaryLine[] = [
        { title: "Role", description: "Associate Software Engineer" },
        { title: "Type", description: "Live Service Co-Development" },
        { title: "Platform", description: "Mobile Game (iOS, Android), PC Game (Windows)" },
        { title: "Language", description: "C#" },
        { title: "Software", description: "Unity, Jira, App Store Connect, Honeycomb" },
        { title: "Duration", description: "18 months" },
    ];

    const imageFolderPath : String = "/img/Projects/MarvelSnap";
    const backgroundPath : String = `${imageFolderPath}/Background`;
    const contentPath : String = `${imageFolderPath}/Content`;

    return (
        <ProjectRoute 
            imagesSrc={["Gameplay.jpg", "CardDetails.jpg", "Cards.jpg", "PlayScreen.jpg"]
                .map(str => `${backgroundPath}/${str}`)}  
            title="MARVEL SNAP"
            summary={summary}
        >
            <Container fluid>
                <h2 className="py-2">About</h2>
                <Row className='px-0 pb-3'>
                    <Col xs={12}>
                        <div className='video-container'>
                            <iframe title='Marvel Snap Trailer' className='px-0 video'
                                src="https://www.youtube.com/embed/GNuq6E3enZQ?autoplay=1&mute=1&playlist=GNuq6E3enZQ&loop=1"
                                allowFullScreen>
                            </iframe>
                        </div>
                    </Col>
                </Row>
                <p>
                    <i>MARVEL SNAP</i> is a fast-paced collectible card game developed by Second Dinner, featuring characters and properties from the Marvel Cinematic Universe.
                    This mobile and PC game has millions of downloads worldwide since its release in October 2022, and thousands of active daily players.
                </p>
                <p>
                    As an Associate Software Engineer at Demiurge Studios, I worked as an individual contributor on the project throughout an 18-month co-development contract,
                    delivering fullstack feature development for the live service. Working within a multidisciplinary team, I built new features, integrated third-party API, and authored 
                    technical documentation to support the game's continued development.
                </p>
                <Container fluid className='px-3 pb-3'>
                    <StoreButtons 
                        steamLink="https://store.steampowered.com/app/1997040/MARVEL_SNAP/"
                        appleLink="https://apps.apple.com/us/app/marvel-snap/id1592081003"
                        googleLink="https://play.google.com/store/apps/details?id=com.nvsgames.snap&hl=en_US"
                    />
                </Container>
            </Container>
            <Container fluid>
                <h2 id="fullstack" className="py-2">Fullstack Feature Development</h2>
                <p>
                    My team shipped various meta game features for this launched title; several of these efforts are shown below, 
                    including my specific contributions to each.
                </p>
                <Row className='px-0 pb-3 justify-content-center'>
                    <Col md={12} lg={6} xl={4}>
                        <CaptionedImage imageSrc={`${contentPath}/CollectorsVault.png`} altText="Collector's Vault" showTitle>
                            <p className='mb-0'>A themed shop section for limited time re-runs of past Cards.</p>
                            <ul className='mb-0'>
                                <li>Created a new layout, optimized for landscape or portrait.</li>
                                <li>Wrote card selection algorithm and pity system.</li>
                                <li>Created authoring tools to control cards rarity and cost.</li>
                            </ul>
                        </CaptionedImage>
                    </Col>
                    <Col md={12} lg={6} xl={4}>
                        <CaptionedImage imageSrc={`${contentPath}/MysteryBorder.png`} altText="Mystery Borders" showTitle>
                            <p className='mb-0'>A mystery box reward type for in-game cosmetics.</p>
                            <ul className='mb-0'>
                                <li>Wrote border selection algorithm.</li>
                                <li>Created authoring tools to opt cosmetics into drop pools.</li>
                            </ul>
                        </CaptionedImage>
                    </Col>
                    <Col md={12} lg={6} xl={4}>
                        <CaptionedImage imageSrc={`${contentPath}/AlbumQuickPreview.png`} altText="Album Quick Preview" showTitle>
                            <p className='mb-0'>An overlay used to preview collection stats of album cards.</p>
                            <ul className='mb-0'>
                                <li>Extended album screen's view models to drive the overlay.</li>
                                <li>Refactored deep link system to allow return navigation between pages.</li>
                            </ul>
                        </CaptionedImage>
                    </Col>
                    <Col md={12} lg={6} xl={4}>
                        <CaptionedImage imageSrc={`${contentPath}/EventBadges.png`} altText="Event Badges" showTitle>
                            <p className='mb-0'>Dynamic buttons to drive visibility to limited time content from the Main Menu.</p>
                            <ul className='mb-0'>
                                <li>Retroactively adapted a pattern for previously non-standardized limited time content types.</li>
                                <li>Extended timers with a condensed, single-unit format.</li>
                            </ul>
                        </CaptionedImage>
                    </Col>
                    <Col md={12} lg={6} xl={4}>
                        <CaptionedImage imageSrc={`${contentPath}/WinBackExperience.png`} altText="Win Back Experience" showTitle>
                            <p className='mb-0'>An overhaul to the returning player experience, including unique rewards, discounts, and missions.</p>
                            <ul className='mb-0'>
                                <li>Defined shared state and communication mechanisms across independent subsystems.</li>
                                <li>Built the centralized hub for overview of win back content.</li>
                            </ul>
                        </CaptionedImage>
                    </Col>
                    <Col md={12} lg={6} xl={4}>
                        <CaptionedImage imageSrc={`${contentPath}/Achievements.png`} altText="Achievement System" showTitle>
                            <p className='mb-0'>Achievements for Marvel SNAP on iOS Game Center, designed for easy adaptation to other platforms.</p>
                            <ul className='mb-0'>
                                <li>Integrated Apple Game Center authentication.</li>
                                <li>Developed a system for awarding achievements atop an existing mission structure.</li>
                                <li>Wrote tools to automate the management of App Store Connect achievements.</li>
                            </ul>
                        </CaptionedImage>
                    </Col>
                </Row>
            </Container>
            <Container fluid>
                <h2 id="documentation" className="py-2">Technical Documentation & Designs</h2>
                <p>
                    During my time on the project, technical documentation was written to serve all manner of purposes.
                    Below is a overview of some common documentation styles I authored throughout development.
                </p>
                <Table bordered variant='dark'>
                    <tbody>
                        <tr>
                            <td>
                                <b>Architecture Briefs</b>
                            </td>
                            <td className='pb-0'>
                                <ul>
                                    <li>Implementation proposals for sizeable features and changes, used to relay plans for development and testing to the wider team.</li>
                                    <li>I authored several as part of my team's ongoing feature work, most notably for the Win Back Experience, Apple Game Center Integration, and Achievement System.</li>
                                </ul>
                            </td>
                        </tr>
                        <tr>
                            <td>
                                <b>HOWTO Guides</b>
                            </td>
                            <td className='pb-0'>
                                <ul>
                                    <li>Documentation for product and content teams, used to run tooling and maintain shipped features.</li>
                                    <li>I authored several to help the client make use of our features, most notably for the Collector's Vault, Mystery Borders, and Achievement System.</li>
                                </ul>
                            </td>
                        </tr>
                        <tr>
                            <td>
                                <b>Feature Handoffs</b>
                            </td>
                            <td className='pb-0'>
                                <ul>
                                    <li>Recorded sessions and consolidated relevant documentation to transfer code ownership and knowledge of work my team had done for a smooth transition at the end of our contract period.</li>
                                </ul>
                            </td>
                        </tr>
                    </tbody>
                </Table>
                <p>
                    In addition to these, I submitted my own initiative to overhaul the game's internal cheat system and expand its capabilities to capture the descriptions and notes that our QA teams maintained external to the game. 
                    This plan was later adopted into work for a junior developer on our team, and ultimately improved the experience for developers making use of these cheats.
                </p>
            </Container>
        </ProjectRoute>
    );
}

export default MarvelSnapRoute;